// Product image picker for the product form: preview, choose, replace, remove.
//
// Holds the chosen File only; the form uploads it on save (see ProductForm),
// so cancelling the dialog never leaves an orphan file in storage.

import { useEffect, useMemo, useRef } from 'react';
import { ImageIcon, Upload, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { IMAGE_ACCEPT } from '@/lib/productImages';
import { useProductImage } from '@/components/inventory/ProductImage';

export interface ProductImageFieldProps {
  /** The image the product has now — the stored value (object path or external URL). */
  currentUrl?: string | null;
  /** A newly chosen file, not yet uploaded. */
  file: File | null;
  /** The current image is to be removed on save. */
  removed: boolean;
  onPick: (file: File) => void;
  onRemove: () => void;
  disabled?: boolean;
}

export function ProductImageField({ currentUrl, file, removed, onPick, onRemove, disabled }: ProductImageFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  // The stored image lives in the private bucket; show it through a signed URL.
  const { src: currentSrc } = useProductImage(removed ? null : currentUrl);
  const shown = previewUrl ?? currentSrc;

  return (
    <div className="flex items-start gap-4">
      <div className="w-28 h-28 rounded-lg border border-line bg-canvas overflow-hidden flex items-center justify-center shrink-0">
        {shown ? (
          <img src={shown} alt="Gambar produk" className="w-full h-full object-cover" />
        ) : (
          <ImageIcon size={28} className="text-ink-subtle" strokeWidth={1.5} />
        )}
      </div>
      <div className="space-y-2 min-w-0">
        <p className="text-small font-medium text-ink">Gambar produk</p>
        <p className="text-caption text-ink-subtle">
          JPG, PNG atau WebP. Dikecilkan otomatis sebelum di-upload, jadi foto langsung dari HP boleh.
          Tampil di POS, inventory, dan halaman event.
        </p>
        <div className="flex flex-wrap gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onPick(f);
              e.target.value = '';
            }}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            leftIcon={<Upload size={14} />}
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
          >
            {shown ? 'Ganti gambar' : 'Pilih gambar'}
          </Button>
          {shown && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              leftIcon={<Trash2 size={14} className="text-danger" />}
              onClick={onRemove}
              disabled={disabled}
            >
              Hapus
            </Button>
          )}
        </div>
        {file && <p className="text-caption text-ink-subtle truncate">Baru: {file.name} — tersimpan saat klik Simpan.</p>}
      </div>
    </div>
  );
}
